import {
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	Button,
} from "@/components/primitives";
import { Loader2, CheckCircle } from "lucide-react";

interface SummaryStepProps {
	/**
	 * Reflection title
	 */
	title: string;
	/**
	 * Reflection description
	 */
	description: string;
	/**
	 * Count of selected contributing factors
	 */
	contributingFactorsCount: number;
	/**
	 * Count of selected signals
	 */
	signalsCount: number;
	/**
	 * Count of selected corrective actions
	 */
	correctiveActionsCount: number;
	/**
	 * Whether the reflection is being saved
	 */
	isSaving?: boolean;
	/**
	 * Whether this is a draft that's being finalized
	 */
	isDraft?: boolean;
	/**
	 * Callback to view the reflection report
	 */
	onViewReport: () => void;
	/**
	 * Callback to return to homepage
	 */
	onReturnHome: () => void;
	/**
	 * Callback to view all reflections
	 */
	onViewReflections: () => void;
}

export function SummaryStep({
	title,
	description,
	contributingFactorsCount,
	signalsCount,
	correctiveActionsCount,
	isSaving = false,
	isDraft = false,
	onViewReport,
	onReturnHome,
	onViewReflections,
}: SummaryStepProps) {
	return (
		<>
			<CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20">
				<div className="flex items-center gap-3">
					<div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600">
						<CheckCircle className="h-5 w-5 text-white" />
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-semibold">
							Reflection Complete!
						</CardTitle>
						<CardDescription className="text-sm mt-1">
							{isDraft
								? "Your reflection is being finalized..."
								: "Your reflection has been successfully completed and saved"}
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="pt-6">
				<div className="space-y-6">
					{/* Reflection Summary Box */}
					<div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
						<h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
							{title}
						</h3>
						<p className="text-sm text-green-800 dark:text-green-200 leading-relaxed">
							{description}
						</p>
					</div>

					{/* Statistics Grid */}
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<div className="text-center p-4 rounded-lg bg-muted/30 dark:bg-muted/20 border border-border">
							<div className="text-2xl font-semibold text-foreground">
								{contributingFactorsCount}
							</div>
							<div className="text-sm text-muted-foreground mt-1">
								Contributing Factors
							</div>
						</div>
						<div className="text-center p-4 rounded-lg bg-muted/30 dark:bg-muted/20 border border-border">
							<div className="text-2xl font-semibold text-orange-600 dark:text-orange-400">
								{signalsCount}
							</div>
							<div className="text-sm text-muted-foreground mt-1">Signals</div>
						</div>
						<div className="text-center p-4 rounded-lg bg-muted/30 dark:bg-muted/20 border border-border">
							<div className="text-2xl font-semibold text-green-600 dark:text-green-400">
								{correctiveActionsCount}
							</div>
							<div className="text-sm text-muted-foreground mt-1">
								Corrective Actions
							</div>
						</div>
					</div>

					{/* Completion Message */}
					<div className="text-center space-y-4">
						{/* Loading indicator when saving or finalizing draft */}
						{(isSaving || isDraft) && (
							<div className="flex items-center justify-center py-4">
								<Loader2 className="h-5 w-5 animate-spin mr-2 text-primary" />
								<span className="text-sm text-muted-foreground">
									Finalizing your reflection...
								</span>
							</div>
						)}

						{!isSaving && !isDraft && (
							<>
								<p className="text-muted-foreground">
									Your reflection has been completed and is ready for daily
									check-ins and monitoring.
								</p>

								{/* Primary Action Button */}
								<div className="pt-2">
									<Button
										size="lg"
										className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all"
										onClick={onViewReport}
									>
										View Reflection Report
									</Button>
								</div>
							</>
						)}

						{/* Secondary Action Buttons */}
						<div className="flex gap-2 pt-2">
							<Button
								variant="outline"
								className="flex-1"
								onClick={onReturnHome}
								disabled={isSaving}
							>
								Return to Homepage
							</Button>
							<Button
								variant="outline"
								className="flex-1"
								onClick={onViewReflections}
								disabled={isSaving}
							>
								View All Reflections
							</Button>
						</div>
					</div>
				</div>
			</CardContent>
		</>
	);
}
